import React, { useMemo } from 'react'
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts'
import {
  BellAlertIcon,
  CheckCircleIcon,
  SparklesIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline'
import { Text, LoadingSpinner } from '../../components/ui'
import type { MarketingNewsAlert, MarketingNewsAlertTriageStatus } from '../../types/marketing'

interface AlertasDashboardTabProps {
  alerts: MarketingNewsAlert[]
  loading?: boolean
}

const TRIAGE_LABELS: Record<MarketingNewsAlertTriageStatus, string> = {
  novo: 'Novo',
  analisado: 'Analisado',
  promovido: 'Clipping',
  descartado: 'Descartado',
}

const TRIAGE_COLORS: Record<MarketingNewsAlertTriageStatus, string> = {
  novo: '#3b82f6',
  analisado: '#f59e0b',
  promovido: '#10b981',
  descartado: '#6b7280',
}

const IA_COLOR_BY_NAME: Record<string, string> = {
  'É clipping': '#10b981',
  'Não é clipping': '#6b7280',
  'Sem análise': '#d1d5db',
}

function formatMonthLabel(yearMonth: string): string {
  const [year, month] = yearMonth.split('-')
  const date = new Date(Number(year), Number(month) - 1, 1)
  return date.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' })
}

function alertMonthKey(alert: MarketingNewsAlert): string | null {
  const raw = alert.published_at || alert.created_at
  if (!raw) return null
  return raw.slice(0, 7)
}

interface KpiCardProps {
  label: string
  value: string
  icon: React.ReactNode
  color: string
}

const KpiCard: React.FC<KpiCardProps> = ({ label, value, icon, color }) => (
  <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-5 flex items-center gap-4">
    <div className={`flex-shrink-0 h-12 w-12 rounded-lg flex items-center justify-center ${color}`}>
      {icon}
    </div>
    <div className="min-w-0">
      <Text size="xs" color="muted" className="uppercase tracking-wider font-medium">
        {label}
      </Text>
      <p className="text-xl font-bold text-gray-900 dark:text-white truncate">{value}</p>
    </div>
  </div>
)

const EmptyChart: React.FC = () => (
  <div className="h-full flex items-center justify-center">
    <Text size="sm" color="muted">
      Nenhum alerta encontrado.
    </Text>
  </div>
)

const AlertasDashboardTab: React.FC<AlertasDashboardTabProps> = ({
  alerts,
  loading = false,
}) => {
  const totalAlertas = alerts.length

  const promovidos = useMemo(
    () => alerts.filter((item) => item.triage_status === 'promovido').length,
    [alerts]
  )

  const promovidosPct = totalAlertas > 0 ? Math.round((promovidos / totalAlertas) * 100) : 0

  const sugestaoClipping = useMemo(
    () => alerts.filter((item) => item.analysis?.is_clipping === true).length,
    [alerts]
  )

  const sugestaoPct = totalAlertas > 0 ? Math.round((sugestaoClipping / totalAlertas) * 100) : 0

  const scoreMedio = useMemo(() => {
    const scored = alerts.filter((item) => item.analysis?.relevance_score != null)
    if (scored.length === 0) return null
    const sum = scored.reduce((acc, item) => acc + (item.analysis?.relevance_score || 0), 0)
    return Math.round(sum / scored.length)
  }, [alerts])

  const byMonthChartData = useMemo(() => {
    if (alerts.length === 0) return []

    const monthKeys = new Set<string>()
    alerts.forEach((item) => {
      const key = alertMonthKey(item)
      if (key) monthKeys.add(key)
    })

    const months = Array.from(monthKeys)
      .sort()
      .map((key) => ({
        key,
        name: formatMonthLabel(key),
        count: 0,
      }))
    const monthMap = new Map(months.map((m) => [m.key, m]))

    alerts.forEach((item) => {
      const key = alertMonthKey(item)
      if (!key) return
      const entry = monthMap.get(key)
      if (entry) entry.count += 1
    })

    return months
  }, [alerts])

  const bySourceChartData = useMemo(() => {
    const map = new Map<string, { name: string; count: number }>()
    alerts.forEach((item) => {
      const name = item.search_site?.vehicle?.name || item.search_site?.name || 'Sem fonte'
      const current = map.get(name) ?? { name, count: 0 }
      current.count += 1
      map.set(name, current)
    })
    return Array.from(map.values()).sort((a, b) => b.count - a.count)
  }, [alerts])

  const byCategoryChartData = useMemo(() => {
    const map = new Map<string, { name: string; count: number }>()
    alerts.forEach((item) => {
      const name = item.analysis?.suggested_category?.name || 'Sem categoria'
      const current = map.get(name) ?? { name, count: 0 }
      current.count += 1
      map.set(name, current)
    })
    return Array.from(map.values()).sort((a, b) => b.count - a.count)
  }, [alerts])

  const byStatusChartData = useMemo(
    () =>
      (Object.keys(TRIAGE_LABELS) as MarketingNewsAlertTriageStatus[])
        .map((status) => ({
          name: TRIAGE_LABELS[status],
          value: alerts.filter((item) => item.triage_status === status).length,
          color: TRIAGE_COLORS[status],
        }))
        .filter((row) => row.value > 0),
    [alerts]
  )

  const byIaSuggestionChartData = useMemo(
    () =>
      [
        {
          name: 'É clipping',
          value: alerts.filter((item) => item.analysis?.is_clipping === true).length,
        },
        {
          name: 'Não é clipping',
          value: alerts.filter((item) => item.analysis?.is_clipping === false).length,
        },
        {
          name: 'Sem análise',
          value: alerts.filter((item) => item.analysis?.is_clipping == null).length,
        },
      ].filter((row) => row.value > 0),
    [alerts]
  )

  if (loading) {
    return (
      <div className="py-16 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label="Total de alertas"
          value={String(totalAlertas)}
          icon={<BellAlertIcon className="h-6 w-6 text-primary-600 dark:text-primary-400" />}
          color="bg-primary-50 dark:bg-primary-900/30"
        />
        <KpiCard
          label="Marcados como clipping"
          value={totalAlertas > 0 ? `${promovidos} (${promovidosPct}%)` : '0'}
          icon={<CheckCircleIcon className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />}
          color="bg-emerald-50 dark:bg-emerald-900/30"
        />
        <KpiCard
          label="Sugestão IA"
          value={totalAlertas > 0 ? `${sugestaoClipping} (${sugestaoPct}%)` : '0'}
          icon={<SparklesIcon className="h-6 w-6 text-purple-600 dark:text-purple-400" />}
          color="bg-purple-50 dark:bg-purple-900/30"
        />
        <KpiCard
          label="Score médio"
          value={scoreMedio != null ? String(scoreMedio) : '—'}
          icon={<ChartBarIcon className="h-6 w-6 text-amber-600 dark:text-amber-400" />}
          color="bg-amber-50 dark:bg-amber-900/30"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="flex items-center justify-between mb-4">
            <Text size="lg" className="text-gray-900 dark:text-white">
              Evolução no tempo
            </Text>
            <Text size="sm" color="muted">
              Alertas por mês
            </Text>
          </div>
          <div className="h-64">
            {byMonthChartData.length === 0 ? (
              <EmptyChart />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={byMonthChartData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" name="Alertas" fill="#008B95" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="flex items-center justify-between mb-4">
            <Text size="lg" className="text-gray-900 dark:text-white">
              Por fonte
            </Text>
            <Text size="sm" color="muted">
              Alertas
            </Text>
          </div>
          <div className="h-64">
            {bySourceChartData.length === 0 ? (
              <EmptyChart />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={bySourceChartData} layout="vertical" margin={{ left: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
                  <XAxis type="number" allowDecimals={false} />
                  <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Bar dataKey="count" name="Alertas" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="flex items-center justify-between mb-4">
            <Text size="lg" className="text-gray-900 dark:text-white">
              Por categoria sugerida
            </Text>
          </div>
          <div className="h-64">
            {byCategoryChartData.length === 0 ? (
              <EmptyChart />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={byCategoryChartData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 10 }}
                    interval={0}
                    angle={-30}
                    textAnchor="end"
                    height={70}
                  />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" name="Alertas" fill="#10b981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="flex items-center justify-between mb-4">
            <Text size="lg" className="text-gray-900 dark:text-white">
              Por status
            </Text>
            <Text size="sm" color="muted">
              Triagem
            </Text>
          </div>
          <div className="h-64">
            {totalAlertas === 0 ? (
              <EmptyChart />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={byStatusChartData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    label
                  >
                    {byStatusChartData.map((row) => (
                      <Cell key={row.name} fill={row.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <Text size="lg" className="text-gray-900 dark:text-white">
              Por sugestão da IA
            </Text>
            <Text size="sm" color="muted">
              Candidato a clipping
            </Text>
          </div>
          <div className="h-64 max-w-md mx-auto">
            {totalAlertas === 0 ? (
              <EmptyChart />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={byIaSuggestionChartData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    label
                  >
                    {byIaSuggestionChartData.map((row) => (
                      <Cell key={row.name} fill={IA_COLOR_BY_NAME[row.name] || '#6b7280'} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default AlertasDashboardTab
